<template>
  <div class="checkpoint-suggestion-content">
    <!-- QuadrilleBackground for Edit Mode -->
    <QuadrilleBackground v-if="isEditMode" className="background-popup" />

    <!-- Modal Header -->
    <div class="modal-header">
    <div class="header-content-wrapper">
      <div class="header-content" :class="{ 'edit-mode-header': isEditMode }">
        <!-- Edit Mode Header (Icon before Title) -->
        <template v-if="isEditMode">
          <!-- Featured Icon -->
          <div class="featured-icon">
            <Icon name="org_icon_checkpoint" />
          </div>
          <div class="header-text">
            <v-card-title class="modal-title">
              Checkpoint Suggestion ({{ currentIndex }}/{{ totalCount }})
            </v-card-title>
          </div>
        </template>

        <!-- Navigate Mode Header (Icon before Title) -->
        <template v-else>
          <!-- Featured Icon -->
          <div class="featured-icon">
            <Icon name="org_icon_checkpoint" />
          </div>
          <!-- Title and Description -->
          <div class="header-text">
            <v-card-title class="modal-title">
              {{
                isAllDone
                  ? "All suggestions were processed"
                  : `${totalCount} Suggested ${
                      totalCount === 1 ? "Checkpoint" : "Checkpoints"
                    } for Responsibility ${responsibilityName}`
              }}
            </v-card-title>
            <v-card-text class="modal-description">
              {{
                isAllDone
                  ? "Close the right pane or ask for more suggestions"
                  : description
              }}
            </v-card-text>
          </div>
        </template>
      </div>
    </div>

    <!-- Close Button -->
    <v-btn icon @click="$emit('close')" color="#98A2B3" class="modal-close-btn">
      <v-icon>mdi-close</v-icon>
    </v-btn>

    <!-- Bottom spacing -->
    <div class="header-bottom-spacing"></div>
  </div>

  <!-- Completion State (when all checkpoints are done) -->
  <div v-if="isAllDone" class="completion-state">
    <div class="completion-actions">
      <div class="completion-actions-left">
        <StandardButton
          type="magenta"
          variant="outline"
          size="full-width"
          label="Suggest More"
          @click="$emit('suggest-more')"
        />
      </div>
      <div class="completion-actions-right">
        <StandardButton
          type="magenta"
          variant="primary"
          size="full-width"
          label="Close"
          @click="$emit('close')"
        />
      </div>
    </div>
  </div>

  <!-- Normal Content (when there are checkpoints left) -->
  <template v-else>
    <!-- Navigation Actions - Navigate Mode -->
    <div v-if="!isEditMode" class="modal-actions">
      <div class="actions-content">
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Edit"
          @click="$emit('edit')"
        />
        <div class="actions-left">
          <span class="counter-text">{{ currentIndex }} of {{ totalCount }}</span>
        </div>

        <div class="actions-right">
          <!-- Back Button -->
          <StandardButton
            type="magenta"
            variant="outline"
            size="normal"
            label=""
            leftIcon="arrowLeft"
            @click="$emit('previous')"
            :class="{ 'invisible-btn': currentIndex <= 1 }"
          />

          <!-- Add Checkpoint Button -->
          <StandardButton
            type="magenta"
            variant="primary"
            size="normal"
            label="Add Checkpoint"
            @click="$emit('add-checkpoint')"
            :loaderLeft="isCreating"
            :disabled="isCreating"
            leftIcon="plusLeft"
          />

          <!-- Reject Button -->
          <StandardButton
            type="magenta"
            variant="outline"
            size="normal"
            label="Reject"
            @click="$emit('reject')"
          />

          <!-- Next Button -->
          <StandardButton
            type="magenta"
            variant="outline"
            size="normal"
            label=""
            rightIcon="arrowRight"
            @click="$emit('next')"
            :class="{ 'invisible-btn': currentIndex >= totalCount }"
          />
        </div>
      </div>
    </div>

    <!-- Edit Actions - Edit Mode -->
    <div v-else class="modal-actions">
      <div class="actions-content edit-actions-content">
        <div class="actions-left">
          <!-- <AskIQButton label="Improve" :height="40" :disabled="true" /> -->
        </div>

        <div class="actions-right edit-actions-buttons">
          <!-- Discard Button -->
          <StandardButton
            type="magenta"
            variant="outline"
            size="normal"
            label="Discard"
            @click="$emit('cancel-edit')"
          />
          <!-- Add Checkpoint Button -->
          <StandardButton
            type="magenta"
            variant="primary"
            size="normal"
            label="Add Checkpoint"
            leftIcon="plusLeft"
            :disabled="!isFormValid || isCreating"
            :loaderLeft="isCreating"
            @click="$emit('save-changes')"
          />
        </div>
      </div>
    </div>

    <!-- Level Band - Navigate Mode -->
    <div v-if="!isEditMode" class="modal-content level-band-content">
      <CheckpointLevelBand :level="currentCheckpoint.level" />
    </div>

    <!-- Level Picker - Edit Mode -->
    <div v-if="isEditMode" class="level-picker-content">
      <CheckpointLevelPicker
        :value="selectedLevelObject"
        :levels="levelOptions"
        label="Select Level"
        @input="$emit('level-change', $event)"
      />
    </div>

    <!-- Criteria Field - Navigate Mode -->
    <div
      v-if="!isEditMode && currentCheckpoint.criteria"
      class="modal-content"
    >
      <div class="field-section">
        <div class="field-header">
          <span class="field-label">Criteria</span>
        </div>
        <span class="field-value">
          <span v-if="currentCheckpoint.prefix" class="criteria-prefix">{{ currentCheckpoint.prefix }} </span>{{ currentCheckpoint.criteria }}
        </span>
      </div>
    </div>

    <!-- Instructions Field - Navigate Mode -->
    <div
      v-if="!isEditMode && currentCheckpoint.instructions"
      class="modal-content"
    >
      <div class="field-section">
        <div class="field-header">
          <span class="field-label">Instructions</span>
        </div>
        <div class="field-value-scrollable">
          <span class="field-value field-value-instructions">
            {{ currentCheckpoint.instructions }}
          </span>
        </div>
      </div>
    </div>

    <!-- Criteria Field - Edit Mode -->
    <div v-if="isEditMode" class="modal-content checkpoint">
      <div class="">
        <ExTextInput
          label="Criteria*"
          placeholder="Add criteria"
          :value="editedCriteria"
          required
          dropdownSide="left"
          :dropdownItems="criteriaDropdownItems"
          class="add-criteria"
          :showCustomIcon="true"
          @dropdown-input="$emit('update:editedCriteriaPrefix', $event)"
          :selectedItem="editedCriteriaPrefix"
          @input="$emit('update:editedCriteria', $event)"
        ></ExTextInput>
      </div>
    </div>

    <!-- Instructions Field - Edit Mode -->
    <div v-if="isEditMode" class="modal-content instructions-edit-section">
      <div class="field-section">
        <div class="field-header">
          <span class="field-label">Instructions</span>
        </div>
        <TiptapEditor
          :value="editedInstructions"
          placeholder="Enter instructions"
          :editable="true"
          @input="$emit('update:editedInstructions', $event)"
          class="edit-tiptap-editor"
        />
      </div>
    </div>
  </template>
  </div>
</template>

<script setup>
import { ref } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import Icon from "@components/icons/Icon.vue";
import TiptapEditor from "@components/TiptapEditor/TiptapEditor.vue";
import QuadrilleBackground from "@components/svgs/QuadrilleBackground.vue";
import CheckpointLevelBand from "@components/CheckpointLevelBand/CheckpointLevelBand.vue";
import CheckpointLevelPicker from "@components/CheckpointLevelPicker/CheckpointLevelPicker.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
defineProps({
  isEditMode: {
    type: Boolean,
    required: true,
  },
  isAllDone: {
    type: Boolean,
    required: true,
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
  isFormValid: {
    type: Boolean,
    default: false,
  },
  currentIndex: {
    type: Number,
    required: true,
  },
  totalCount: {
    type: Number,
    required: true,
  },
  responsibilityName: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: "",
  },
  currentCheckpoint: {
    type: Object,
    required: true,
  },
  editedInstructions: {
    type: String,
    default: "",
  },
  selectedLevelObject: {
    type: Object,
    default: null,
  },
  levelOptions: {
    type: Array,
    default: () => [],
  },
  editedCriteria: {
    type: String,
    default: "",
  },
  editedCriteriaPrefix: {
    type: String,
    default: "Demonstrated",
  },
});

const criteriaDropdownItems = ref([
  "Can",
  "Demonstrated",
  "Proved",
  "Does"
]);

defineEmits([
  "close",
  "suggest-more",
  "edit",
  "cancel-edit",
  "save-changes",
  "add-checkpoint",
  "reject",
  "previous",
  "next",
  "level-change",
  "update:editedInstructions",
  "update:editedCriteria",
  "update:editedCriteriaPrefix",
]);
</script>

<style scoped>
@import "./checkpointSuggestionModal.css";

/* Criteria Edit Section */
.checkpoint ::v-deep .v-text-field {
  padding-top: 0px !important;
}

.checkpoint ::v-deep .level-picker.v-text-field .v-input__control .v-input__slot .v-input__append-inner,
.checkpoint ::v-deep .level-picker.v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  margin-top: 4px;
}
::v-deep .add-criteria .left-demonstrated-prepand {
  min-width: 132px;
  top: -10px;
}
::v-deep .add-criteria .left-proved-prepand {
  min-width: 80px;
  top: -10px;
}

/* Dropdown z-index to show above instructions */
.modal-content.checkpoint {
  position: relative;
  z-index: 50;
  overflow: visible !important;
}

::v-deep .add-criteria {
  overflow: visible !important;
}

::v-deep .add-criteria .code-select-wrapper {
  overflow: visible !important;
}

::v-deep .add-criteria .v-menu__content {
  z-index: 9999 !important;
  position: absolute !important;
}

::v-deep .add-criteria .v-select__content {
  z-index: 9999 !important;
}

::v-deep .add-criteria .v-list {
  z-index: 9999 !important;
}

.instructions-edit-section {
  position: relative;
  z-index: 1 !important;
  overflow: hidden;
}

.instructions-edit-section .edit-tiptap-editor {
  max-height: 250px;
  overflow: hidden;
}
::v-deep .add-criteria.ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -60px !important;
}
::v-deep .add-criteria.ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -10px !important;
}
</style>

